/* Accessible audience tabs, with independent, duplicate-free collection paging. */
(() => {
  const instances = new Map();
  function init(root) {
    if (instances.has(root)) return;
    const lifetime = new AbortController();
    const { signal } = lifetime;
    const tabs = [...root.querySelectorAll('[data-vision-tab]')];
    const panels = [...root.querySelectorAll('[data-vision-panel]')];
    const tablist = root.querySelector('[data-vision-tabs]');
    if (!tabs.length || tabs.length !== panels.length || !tablist) return;
    instances.set(root, lifetime);

    const select = (key, focus = false, updateHistory = false) => {
      const selected = tabs.find(tab => tab.dataset.visionTab === key) || tabs.find(tab => tab.dataset.visionTab === root.dataset.defaultTab) || tabs[0];
      tabs.forEach((tab, index) => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        panels[index].hidden = !active;
      });
      if (focus) selected.focus({ preventScroll: true });
      if (updateHistory && location.hash !== selected.hash) history.pushState(null, '', selected.hash);
    };
    const selectFromLocation = () => {
      let key = location.hash.slice(1);
      // Preserve existing quote links and Shopify contact-form result anchors.
      const target = document.getElementById(key);
      const targetPanel = target?.closest('[data-vision-panel]');
      if (targetPanel) key = targetPanel.dataset.visionPanel;
      select(key || root.dataset.defaultTab);
      if (target?.matches('form, details')) {
        const details = target.matches('details') ? target : target.closest('details');
        if (details) { details.open = true; details.scrollIntoView({ block: 'center' }); }
      }
      if (key === 'quote' || key === 'event-quote') {
        const panel = panels.find(p => !p.hidden);
        panel.querySelector('details').open = true;
        panel.querySelector('details').scrollIntoView({ block: 'center' });
      }
    };
    tablist.setAttribute('role', 'tablist');
    tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panels[index].id);
      panels[index].setAttribute('role', 'tabpanel');
      // Links within each panel provide keyboard entry; the whole panel is not a tab stop.
      tab.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        select(tab.dataset.visionTab, false, true);
      }, { signal });
      tab.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        select(tabs[next].dataset.visionTab, true, true);
      }, { signal });
    });
    window.addEventListener('popstate', selectFromLocation, { signal });
    window.addEventListener('hashchange', selectFromLocation, { signal });
    selectFromLocation();
    root.querySelectorAll('[data-quote-result]').forEach(result => { result.closest('details').open = true; });
    root.addEventListener('submit', event => {
      const form = event.target.closest('.pre-vision-quote__form');
      if (form) form.action = `${form.action.split('#')[0]}#${form.id}`;
    }, { signal });

    panels.forEach(panel => {
      const shop = panel.querySelector('[data-vision-shop]');
      const grid = shop.querySelector('[data-vision-grid]');
      const button = shop.querySelector('[data-vision-more]');
      const count = shop.querySelector('[data-vision-count]');
      const status = shop.querySelector('[data-vision-status]');
      const fallback = shop.querySelector('[data-vision-fallback]');
      const total = Number(shop.dataset.total) || null;
      const sources = JSON.parse(shop.querySelector('[data-vision-sources]').textContent).filter(Boolean);
      const seen = new Set([...grid.querySelectorAll('[data-card]')].map(card => card.dataset.handle));
      let state = { source: 0, page: 2, queue: [] };
      let loading = false;
      if (!sources.length) return;
      // The first eight cards are already rendered by Liquid in the primary collection's order.
      if (Number(shop.dataset.firstTotal) <= 8) state = { source: 1, page: 1, queue: [] };
      button.hidden = !button.hasAttribute('data-has-more');
      fallback.hidden = true;
      button.addEventListener('click', async () => {
        if (loading) return;
        loading = true;
        button.disabled = true;
        button.textContent = 'Loading…';
        shop.setAttribute('aria-busy', 'true');
        status.textContent = '';
        // Work on a copy so a network error cannot skip products on a retry.
        const next = { ...state, queue: [...state.queue] };
        const added = [];
        const batchHandles = new Set(seen);
        const requested = new Set();
        const controller = new AbortController();
        const stop = () => controller.abort();
        signal.addEventListener('abort', stop, { once: true });
        const timeout = setTimeout(stop, 30000);
        try {
          while (added.length < 8) {
            while (next.queue.length && added.length < 8) {
              const card = next.queue.shift();
              const handle = card.dataset.handle;
              if (!handle || batchHandles.has(handle)) continue;
              batchHandles.add(handle);
              added.push(card);
            }
            if (added.length === 8 || next.source >= sources.length) break;
            const url = new URL(sources[next.source], location.origin);
            if (url.origin !== location.origin) throw new Error('Invalid collection origin');
            url.searchParams.set('section_id', 'pre-vision-products');
            url.searchParams.set('page', String(next.page));
            if (requested.has(url.href)) throw new Error('Repeated collection page');
            requested.add(url.href);
            const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
            if (!response.ok) throw new Error('Collection request failed');
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const result = doc.querySelector('[data-vision-result]');
            const handle = url.pathname.split('/').filter(Boolean).pop();
            if (!result || result.dataset.collection !== handle || Number(result.dataset.page) !== next.page) throw new Error('Invalid collection response');
            next.queue.push(...result.querySelectorAll('[data-card]'));
            const following = Number(result.dataset.next);
            if (following) {
              if (following <= next.page) throw new Error('Invalid next page');
              next.page = following;
            } else {
              next.source++;
              next.page = 1;
            }
          }
          if (signal.aborted) return;
          grid.append(...added);
          added.forEach(card => seen.add(card.dataset.handle));
          state = next;
          grid.dispatchEvent(new CustomEvent('everla:cards-added', { bubbles: true }));
          count.textContent = `Showing ${seen.size}${total ? ` of ${total}` : ''} pieces`;
          const done = state.source >= sources.length && !state.queue.length;
          button.hidden = done;
          fallback.hidden = true;
          status.textContent = done ? 'You’ve seen the full selection.' : `${added.length} more pieces added.`;
          // Move focus to the first new item so keyboard users can browse the appended products.
          added[0]?.querySelector('a')?.focus({ preventScroll: true });
        } catch (error) {
          if (!signal.aborted) {
            status.textContent = 'We couldn’t load the next pieces. Please try again, or open the full collection below.';
            fallback.hidden = false;
          }
        } finally {
          clearTimeout(timeout);
          signal.removeEventListener('abort', stop);
          loading = false;
          button.disabled = false;
          button.textContent = 'Load more';
          shop.removeAttribute('aria-busy');
        }
      }, { signal });
    });
  }
  document.querySelectorAll('[data-vision]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-vision]').forEach(init));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((controller, root) => {
      if (event.target.contains(root)) { controller.abort(); instances.delete(root); }
    });
  });
})();
